import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import useTicketingAccess from "./useTicketingAccess";

/**
 * Determines whether the current user is allowed to view a specific ticket.
 *
 * Access is granted when the user is:
 *  1. A Sustain Team member (`isSustainTeamMember`)
 *  2. A Super User (`isSuperUser`) — read-only access
 *  3. The ticket is an active widespread incident (`isActiveWidespreadTicket`)
 *  4. The ticket submitter (`ticket.submittedBy` matches current user email)
 *  5. A secondary stakeholder (listed in the comma-separated `ticket.stakeholders` field)
 *  6. Navigated from the Submit Ticket search panel (`ticketDetailSource === "Submit Ticket"`)
 *     — any authenticated user may preview a ticket they found via the public search.
 *
 * While the ticket is still loading (`submittedBy` is empty) the hook returns
 * `hasAccess: true` so that the screen is never blocked during the initial fetch.
 */
export function useTicketViewAccess(
  submittedBy: string,
  stakeholders?: string,
  isActiveWidespreadTicket: boolean = false,
  isDeepLinked: boolean = false
) {
  const currentUserEmail = useSelector(
    (state: RootState) => state.appData?.userInfo?.email ?? ""
  );
  const noAccessMode = useSelector(
    (state: RootState) => (state.appData as any)?.noAccessMode === true);
  const ticketDetailSource = useSelector(
    (state: RootState) => state.ticketingPortal?.ticketDetailSource ?? ""
  );
  const { isSustainTeamMember, isSuperUser } = useTicketingAccess();

  // Consider the ticket loaded once submittedBy is populated from the API
  const isTicketLoaded = !!submittedBy;

  const isTicketSubmitter = useMemo(
    () =>
      isTicketLoaded &&
      !!currentUserEmail &&
      submittedBy.trim().toLowerCase() === currentUserEmail.trim().toLowerCase(),
    [isTicketLoaded, submittedBy, currentUserEmail]
  );

  const isSecondaryStakeholder = useMemo(() => {
    if (!isTicketLoaded || !currentUserEmail || !stakeholders) return false;
    const stakeholderList = stakeholders
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    return stakeholderList.includes(currentUserEmail.trim().toLowerCase());
  }, [isTicketLoaded, currentUserEmail, stakeholders]);

  // Basic-access (no Navi) user viewing their own ticket from My Tickets — read-only preview.
  const isFromMyTicketsView = ticketDetailSource === "My Tickets" && noAccessMode;

  // Any authenticated user who clicked "View Details" from the Submit Ticket
  // search panel (RelatedIssuesPanel) should be able to preview the ticket.
  const isFromSubmitTicketSearch = ticketDetailSource === "Submit Ticket";

  const hasAccess =
    !isTicketLoaded || // still loading — don't block
    isFromMyTicketsView ||// still loading — don't block
    isActiveWidespreadTicket ||
    isDeepLinked || // deep-linked tickets are publicly accessible
    isFromSubmitTicketSearch || // arrived via the public search panel — read-only preview
    isSustainTeamMember ||
    isSuperUser ||     // Super Users have read-only access to all tickets
    isTicketSubmitter ||
    isSecondaryStakeholder;

  return {
    hasAccess,
    isTicketLoaded,
    isTicketSubmitter,
    isSecondaryStakeholder,
    isSuperUser,
  };
}
